/**
 * Standard utility for handling standardized API responses in React components.
 */

/**
 * Extracts a single error message for a specific field.
 * Useful for displaying validation errors under input fields.
 * 
 * @param {Object} apiError - The error object rejected by axios
 * @param {string} field - The field name (e.g., 'email')
 * @returns {string|null}
 */
export const getFieldError = (apiError, field) => {
  if (!apiError) return null;
  const data = apiError.response?.data || apiError;
  if (data?.errors && data.errors[field]) {
    const error = data.errors[field];
    return Array.isArray(error) ? error[0] : error;
  }
  if (data?.[field]) {
    const error = data[field];
    return Array.isArray(error) ? error[0] : error;
  }
  return null;
};

/**
 * Extracts the main message from an API error.
 * 
 * @param {Object} apiError 
 * @param {string} fallback 
 * @returns {string}
 */
export const getErrorMessage = (apiError, fallback = "Something went wrong") => {
  if (apiError?.errors) {
    const errorMessages = Object.values(apiError.errors).flat();
    if (errorMessages.length > 0) {
      return `${apiError.message || fallback}\n- ${errorMessages.join('\n- ')}`;
    }
  }
  return apiError?.message || fallback;
};

/**
 * Normalizes pagination meta data for UI components.
 * 
 * @param {Object} meta - The meta object from API response
 * @returns {Object}
 */
export const normalizePagination = (meta) => {
  if (!meta) return { current: 1, total: 0, lastPage: 1 };
  return {
    current: meta.current_page,
    lastPage: meta.last_page,
    perPage: meta.per_page,
    total: meta.total
  };
};

/**
 * Checks if an error is a validation error (422).
 */
export const isValidationError = (error) => error?.status === 422;

/**
 * Checks if an error is an authentication error (401).
 */
export const isAuthError = (error) => error?.status === 401;
